import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import SchemaMarkup, { BreadcrumbSchema, AggregateRatingSchema } from '../components/SchemaMarkup';
import './NewsDetail.css';

const fallbackImage = `${process.env.PUBLIC_URL || ''}/assets/banner.png`;

const sanitizeArticleHtml = (html) => {
  if (!html) return '';

  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*("|')\s*javascript:[^"']*("|')/gi, '$1="#"');
};
const defaultTitle = "Alberta Class 7 Practice Test | Free 2026 Learner's Prep";
const defaultDescription =
  "Alberta Class 7 driving test prep with 2026-updated practice exams. Free mock test, 500+ real-style questions, and clear explanations to help you pass your learner's knowledge test on the first try.";

const stripHtml = (value = '') => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const truncateText = (value = '', maxLength = 160) => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 3).trim()}...`;
};

const toAbsoluteUrl = (value = '') => {
  if (!value) {
    return '';
  }

  if (typeof window === 'undefined') {
    return value;
  }

  try {
    return new URL(value, window.location.origin).href;
  } catch {
    return value;
  }
};

const buildPlainExcerpt = (value = '', maxLength = 80) => {
  const plain = stripHtml(value || '').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  if (!plain) {
    return 'Read this article for more driving tips.';
  }

  return plain.length > maxLength ? `${plain.slice(0, maxLength - 3).trim()}...` : plain;
};

const getMetaContent = (selector) => document.head.querySelector(selector)?.getAttribute('content') || null;

const upsertMetaTag = ({ name, property, content }) => {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let meta = document.head.querySelector(selector);

  if (!meta) {
    meta = document.createElement('meta');
    if (name) {
      meta.setAttribute('name', name);
    }
    if (property) {
      meta.setAttribute('property', property);
    }
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', content || '');
};

const upsertCanonicalLink = (href) => {
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', href || '');
};

const NewsDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRated, setUserRated] = useState(false);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setIsLoading(true);
        setError('');

        const response = await fetch(`/api/news?slug=${encodeURIComponent(slug)}`);
        const data = await response.json();

        if (!response.ok) {
          setError(data.message || 'Unable to load this article right now.');
          return;
        }

        setArticle(data);
      } catch (fetchError) {
        console.error('Error fetching news detail:', fetchError);
        setError('Unable to load this article right now.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  // Fetch related articles when main article is loaded
  useEffect(() => {
    if (!article || !article.category_id) return;

    const fetchRelatedArticles = async () => {
      try {
        const response = await fetch(`/api/news?limit=10`);
        const data = await response.json();

        if (!response.ok) {
          setRelatedArticles([]);
          return;
        }

        // Filter articles: same category, published, and not the current article
        const related = (Array.isArray(data) ? data : [])
          .filter(
            (item) =>
              item.category_id === article.category_id &&
              item.id !== article.id &&
              item.is_published
          )
          .slice(0, 3); // Limit to 3 related articles

        setRelatedArticles(related);
      } catch (err) {
        console.error('Error fetching related articles:', err);
        setRelatedArticles([]);
      }
    };

    fetchRelatedArticles();
  }, [article]);

  const publishedDate = useMemo(() => {
    if (!article?.created_at) {
      return '';
    }

    return new Date(article.created_at).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [article]);

  useEffect(() => {
    if (!article || typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const previousTitle = document.title;
    const previousCanonical =
      document.head.querySelector('link[rel="canonical"]')?.getAttribute('href') || null;
    const previousMetaValues = {
      description: getMetaContent('meta[name="description"]'),
      ogTitle: getMetaContent('meta[property="og:title"]'),
      ogDescription: getMetaContent('meta[property="og:description"]'),
      ogType: getMetaContent('meta[property="og:type"]'),
      ogUrl: getMetaContent('meta[property="og:url"]'),
      ogImage: getMetaContent('meta[property="og:image"]'),
      ogImageSecure: getMetaContent('meta[property="og:image:secure_url"]'),
      ogImageAlt: getMetaContent('meta[property="og:image:alt"]'),
      ogImageWidth: getMetaContent('meta[property="og:image:width"]'),
      ogImageHeight: getMetaContent('meta[property="og:image:height"]'),
      ogSiteName: getMetaContent('meta[property="og:site_name"]'),
      ogPublishedTime: getMetaContent('meta[property="article:published_time"]'),
      twitterCard: getMetaContent('meta[name="twitter:card"]'),
      twitterTitle: getMetaContent('meta[name="twitter:title"]'),
      twitterDescription: getMetaContent('meta[name="twitter:description"]'),
      twitterImage: getMetaContent('meta[name="twitter:image"]'),
    };

    const pageUrl = window.location.href;
    const pageTitle = `${article.title} | Alberta Class 7 Driving News`;
    const pageDescription = truncateText(stripHtml(article.content || ''), 160) || defaultDescription;
    const imageUrl = toAbsoluteUrl(article.img || fallbackImage);
    const publishedTime = article.created_at ? new Date(article.created_at).toISOString() : '';

    document.title = pageTitle;
    upsertCanonicalLink(pageUrl);

    upsertMetaTag({ name: 'description', content: pageDescription });
    upsertMetaTag({ property: 'og:title', content: pageTitle });
    upsertMetaTag({ property: 'og:description', content: pageDescription });
    upsertMetaTag({ property: 'og:type', content: 'article' });
    upsertMetaTag({ property: 'og:url', content: pageUrl });
    upsertMetaTag({ property: 'og:image', content: imageUrl });
    upsertMetaTag({ property: 'og:image:secure_url', content: imageUrl });
    upsertMetaTag({ property: 'og:image:alt', content: article.title || defaultTitle });
    upsertMetaTag({ property: 'og:image:width', content: '1200' });
    upsertMetaTag({ property: 'og:image:height', content: '630' });
    upsertMetaTag({ property: 'og:site_name', content: 'Alberta Class 7 Practice Test' });
    upsertMetaTag({ property: 'article:published_time', content: publishedTime });

    upsertMetaTag({ name: 'twitter:card', content: 'summary_large_image' });
    upsertMetaTag({ name: 'twitter:title', content: pageTitle });
    upsertMetaTag({ name: 'twitter:description', content: pageDescription });
    upsertMetaTag({ name: 'twitter:image', content: imageUrl });

    return () => {
      document.title = previousTitle || defaultTitle;
      upsertCanonicalLink(previousCanonical || window.location.origin);

      upsertMetaTag({ name: 'description', content: previousMetaValues.description || defaultDescription });
      upsertMetaTag({ property: 'og:title', content: previousMetaValues.ogTitle || defaultTitle });
      upsertMetaTag({
        property: 'og:description',
        content: previousMetaValues.ogDescription || defaultDescription,
      });
      upsertMetaTag({ property: 'og:type', content: previousMetaValues.ogType || 'website' });
      upsertMetaTag({ property: 'og:url', content: previousMetaValues.ogUrl || window.location.origin });
      upsertMetaTag({ property: 'og:image', content: previousMetaValues.ogImage || '' });
      upsertMetaTag({ property: 'og:image:secure_url', content: previousMetaValues.ogImageSecure || '' });
      upsertMetaTag({ property: 'og:image:alt', content: previousMetaValues.ogImageAlt || '' });
      upsertMetaTag({ property: 'og:image:width', content: previousMetaValues.ogImageWidth || '' });
      upsertMetaTag({ property: 'og:image:height', content: previousMetaValues.ogImageHeight || '' });
      upsertMetaTag({ property: 'og:site_name', content: previousMetaValues.ogSiteName || '' });
      upsertMetaTag({
        property: 'article:published_time',
        content: previousMetaValues.ogPublishedTime || '',
      });

      upsertMetaTag({ name: 'twitter:card', content: previousMetaValues.twitterCard || 'summary_large_image' });
      upsertMetaTag({ name: 'twitter:title', content: previousMetaValues.twitterTitle || defaultTitle });
      upsertMetaTag({
        name: 'twitter:description',
        content: previousMetaValues.twitterDescription || defaultDescription,
      });
      upsertMetaTag({ name: 'twitter:image', content: previousMetaValues.twitterImage || '' });
    };
  }, [article]);

  return (
    <div className="news-detail-page">
      <Header />
      <main className="news-detail-main">
        {isLoading && <p className="news-detail-state">Loading article...</p>}

        {!isLoading && error && (
          <div className="news-detail-error-box">
            <h2>Article Not Available</h2>
            <p>{error}</p>
            <button className="news-detail-back-btn" onClick={() => navigate('/')}>
              Back to Landing Page
            </button>
          </div>
        )}

        {!isLoading && !error && article && (
          <>
            {/* Schema Markup for SEO */}
            <SchemaMarkup
              type="NewsArticle"
              title={article.title}
              description={article.content?.substring(0, 160)}
              image={article.img || fallbackImage}
              datePublished={article.created_at}
              dateModified={article.updated_at || article.created_at}
              url={typeof window !== 'undefined' ? window.location.href : ''}
              content={article.content}
              aggregateRating={
                article.average_rating > 0
                  ? {
                      ratingValue: article.average_rating,
                      ratingCount: article.rating_count || 0,
                    }
                  : null
              }
            />

            <BreadcrumbSchema
              items={[
                { name: 'Home', url: `${typeof window !== 'undefined' ? window.location.origin : ''}/` },
                { name: 'News', url: `${typeof window !== 'undefined' ? window.location.origin : ''}/news` },
                { name: article.title, url: typeof window !== 'undefined' ? window.location.href : '' },
              ]}
            />

            <AggregateRatingSchema
              ratingValue={article.average_rating || 0}
              ratingCount={article.rating_count || 0}
              itemName={article.title}
              itemUrl={typeof window !== 'undefined' ? window.location.href : ''}
            />

            <article className="news-article">
              <div className="news-article-topbar">
                <button className="news-detail-back-btn" onClick={() => navigate('/')}>
                  Back
                </button>
                {article.category_name && (
                  <span className="news-article-category">{article.category_name}</span>
                )}
              </div>

              <h1 className="news-article-title">{article.title}</h1>

              <div className="news-article-meta">
                <span>{publishedDate}</span>
                <span>{Number(article.views || 0).toLocaleString()} views</span>
              </div>

              <img
                className="news-article-image"
                src={article.img || fallbackImage}
                alt={article.title}
                loading="lazy"
              />

              <div
                className="news-article-content"
                dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(article.content) }}
              />

              {/* Rating Section */}
              <div className="news-article-rating-box">
                <h3>Rate This Article</h3>
                <div className="news-article-rating-container">
                  {article.average_rating > 0 && (
                    <div className="news-article-rating-display">
                      <div className="news-article-stars-display">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            className={`news-article-star ${
                              star <= Math.round(article.average_rating) ? 'filled' : ''
                            }`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                      <span className="news-article-rating-info">
                        {article.average_rating.toFixed(1)} based on {article.rating_count || 0}{' '}
                        {article.rating_count === 1 ? 'rating' : 'ratings'}
                      </span>
                    </div>
                  )}

                  {!userRated ? (
                    <div className="news-article-stars-interactive">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          className="news-article-star-btn"
                          onClick={() => {
                            setUserRated(true);
                            setTimeout(() => {
                              setUserRated(false);
                            }, 2000);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#ffc107';
                            for (let i = 1; i <= star; i++) {
                              e.currentTarget.parentElement.children[i - 1].style.color = '#ffc107';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.parentElement
                              .querySelectorAll('.news-article-star-btn')
                              .forEach((btn) => {
                                btn.style.color = '#cccccc';
                              });
                          }}
                          aria-label={`Rate this article ${star} stars`}
                          title="Click to rate"
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="news-article-rating-feedback">
                      <span className="news-article-rating-success">✓ Thank you for rating!</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="news-article-cta">
                <h3>Continue Your Class 7 Journey</h3>
                <p>Read more practical tips and then start practicing with official-style questions.</p>
                <button className="news-article-practice-btn" onClick={() => navigate('/practice')}>
                  Start Practice Test
                </button>
              </div>

              {/* Related Articles Section */}
              {relatedArticles.length > 0 && (
                <div className="news-article-related">
                  <h3 className="news-article-related-title">Related Articles</h3>
                  <div className="news-article-related-grid">
                    {relatedArticles.map((relatedArticle) => (
                      <div
                        key={relatedArticle.id}
                        className="news-article-related-card"
                        onClick={() => navigate(`/news/${relatedArticle.slug}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            navigate(`/news/${relatedArticle.slug}`);
                          }
                        }}
                      >
                        <div className="news-article-related-image">
                          <img
                            src={relatedArticle.img || fallbackImage}
                            alt={relatedArticle.title}
                            loading="lazy"
                          />
                          {relatedArticle.category_name && (
                            <span className="news-article-related-category">
                              {relatedArticle.category_name}
                            </span>
                          )}
                        </div>
                        <div className="news-article-related-content">
                          <h4 className="news-article-related-card-title">{relatedArticle.title}</h4>
                          <div className="news-article-related-meta">
                            <span>
                              {new Date(relatedArticle.created_at).toLocaleDateString('en-CA', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                            {relatedArticle.average_rating > 0 && (
                              <span className="news-article-related-rating">
                                ⭐ {relatedArticle.average_rating.toFixed(1)}
                              </span>
                            )}
                          </div>
                          <p className="news-article-related-excerpt">
                            {buildPlainExcerpt(relatedArticle.content, 80)}
                          </p>
                        </div>
                        <div className="news-article-related-arrow">→</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </article>
          </>
        )}
      </main>
    </div>
  );
};

export default NewsDetail;
